// Status color utilities for consistent design across the application

// Generic Color Classes Generator
export const getColorClasses = (color) => {
  const colorMap = {
    blue: {
      bgColor: "bg-blue-50 dark:bg-blue-900/30",
      textColor: "text-blue-700 dark:text-blue-400",
      borderColor: "border-blue-200 dark:border-blue-800",
      dotColor: "bg-blue-500",
    },
    green: {
      bgColor: "bg-green-50 dark:bg-green-900/30",
      textColor: "text-green-700 dark:text-green-400",
      borderColor: "border-green-200 dark:border-green-800",
      dotColor: "bg-green-500",
    },
    yellow: {
      bgColor: "bg-yellow-50 dark:bg-yellow-900/30",
      textColor: "text-yellow-700 dark:text-yellow-400",
      borderColor: "border-yellow-200 dark:border-yellow-800",
      dotColor: "bg-yellow-500",
    },
    red: {
      bgColor: "bg-red-50 dark:bg-red-900/30",
      textColor: "text-red-700 dark:text-red-400",
      borderColor: "border-red-200 dark:border-red-800",
      dotColor: "bg-red-500",
    },
    purple: {
      bgColor: "bg-purple-50 dark:bg-purple-900/30",
      textColor: "text-purple-700 dark:text-purple-400",
      borderColor: "border-purple-200 dark:border-purple-800",
      dotColor: "bg-purple-500",
    },
    pink: {
      bgColor: "bg-pink-50 dark:bg-pink-900/30",
      textColor: "text-pink-700 dark:text-pink-400",
      borderColor: "border-pink-200 dark:border-pink-800",
      dotColor: "bg-pink-500",
    },
    gray: {
      bgColor: "bg-gray-50 dark:bg-gray-800/50",
      textColor: "text-gray-700 dark:text-gray-400",
      borderColor: "border-gray-200 dark:border-gray-700",
      dotColor: "bg-gray-500",
    },
    indigo: {
      bgColor: "bg-indigo-50 dark:bg-indigo-900/30",
      textColor: "text-indigo-700 dark:text-indigo-400",
      borderColor: "border-indigo-200 dark:border-indigo-800",
      dotColor: "bg-indigo-500",
    },
    orange: {
      bgColor: "bg-orange-50 dark:bg-orange-900/30",
      textColor: "text-orange-700 dark:text-orange-400",
      borderColor: "border-orange-200 dark:border-orange-800",
      dotColor: "bg-orange-500",
    }
  };

  return colorMap[color] || colorMap.gray;
};

// Project Status Colors
export const getProjectStatusColors = (status) => {
  const statusMap = {
    new: { ...getColorClasses('blue'), label: "New" },
    ongoing: { ...getColorClasses('yellow'), label: "Ongoing" },
    completed: { ...getColorClasses('green'), label: "Completed" },
    cancelled: { ...getColorClasses('red'), label: "Cancelled" }
  };

  return statusMap[status] || { ...getColorClasses('blue'), label: status };
};

// Project Type Colors
export const getProjectTypeColors = (type) => {
  const typeMap = {
    maintenance: { ...getColorClasses('purple'), label: "Maintenance" },
    ongoing: { ...getColorClasses('indigo'), label: "Ongoing" },
    "one-time": { ...getColorClasses('orange'), label: "One-time" }
  };

  return typeMap[type] || { ...getColorClasses('purple'), label: type };
};

// Card status colors (enhanced for better design)
export const getCardStatusColors = (status, colorFallback = null) => {
  const statusMap = {
    todo: { ...getColorClasses('blue'), label: "To Do" },
    doing: { ...getColorClasses('yellow'), label: "In Progress" },
    review: { ...getColorClasses('purple'), label: "Review" },
    done: { ...getColorClasses('green'), label: "Done" },
    archive: { ...getColorClasses('gray'), label: "Archive" }
  };

  // If a specific color is provided, use it (allows overriding default status colors)
  if (colorFallback) {
    const label = statusMap[status]?.label || status;
    return { ...getColorClasses(colorFallback), label };
  }

  if (statusMap[status]) return statusMap[status];
  
  // Default to gray if no status match and no fallback
  return { ...getColorClasses('gray'), label: status };
};

// Priority Colors
export const getPriorityColors = (priority) => {
  const priorityMap = {
    low: { ...getColorClasses('green'), label: "Low" },
    medium: { ...getColorClasses('yellow'), label: "Medium" },
    high: { ...getColorClasses('orange'), label: "High" },
    urgent: { ...getColorClasses('red'), label: "Urgent" }
  };

  return priorityMap[priority] || { ...getColorClasses('yellow'), label: priority };
};

// Status Badge Component Helper
export const getStatusBadgeClasses = (type, status) => {
  const colors = type === 'projectStatus' ? getProjectStatusColors(status) : 
                 type === 'projectType' ? getProjectTypeColors(status) :
                 type === 'cardStatus' ? getCardStatusColors(status) :
                 getPriorityColors(status);
  
  return `inline-flex items-center h-6 px-2.5 rounded-lg text-xs font-medium ${colors.bgColor} ${colors.textColor} ${colors.borderColor} border transition-colors duration-200`;
};

// Status Dot Component Helper
export const getStatusDotClasses = (type, status) => {
  const colors = type === 'projectStatus' ? getProjectStatusColors(status) : 
                 type === 'projectType' ? getProjectTypeColors(status) :
                 type === 'cardStatus' ? getCardStatusColors(status) :
                 getPriorityColors(status);
  
  return `w-2 h-2 rounded-full ${colors.dotColor}`;
};
